import { ChevronDownIcon } from '@heroicons/react/solid';
import useLocale from 'next-translate/useTranslation';
import Image from 'next/image';
import { FC, memo, useEffect, useRef, useState } from 'react';
import { ImageModelType } from 'types';

import clsxm from '@/lib/clsxm';

import { actionsWithDescription } from '@/constants/imageActions';

interface ActionSelectionProps {
  onChange: (value: ImageModelType) => void;
  selectedAction?: string;
}

const FALLBACK_ACTION = actionsWithDescription[0];

function Thumbnail({ src, alt }: { src?: string; alt: string }) {
  if (!src) {
    return (
      <div className='h-10 w-10 flex-shrink-0 rounded-md bg-gradient-to-br from-indigo-100 to-slate-100 dark:from-indigo-900/40 dark:to-slate-800' />
    );
  }
  return (
    <div className='relative h-10 w-10 flex-shrink-0 overflow-hidden rounded-md bg-slate-100 dark:bg-slate-800'>
      <Image src={src} alt={alt} fill sizes='40px' className='object-cover' />
    </div>
  );
}

const ActionSelection: FC<ActionSelectionProps> = ({
  onChange,
  selectedAction = 'Edit Image with AI',
}) => {
  const { t } = useLocale('ai-images');
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const current =
    actionsWithDescription.find((a) => a.title === selectedAction) ??
    FALLBACK_ACTION;

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div ref={containerRef} className='relative w-full'>
      <button
        type='button'
        onClick={() => setOpen((v) => !v)}
        aria-haspopup='listbox'
        aria-expanded={open}
        className={clsxm(
          'flex w-full items-center gap-3 rounded-md border bg-white px-3 py-2 text-left shadow-sm transition-colors',
          'border-gray-300 hover:border-gray-400 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500',
          'dark:border-white/10 dark:bg-slate-700/50 dark:hover:border-white/20',
        )}
      >
        <Thumbnail src={current.image} alt={t(current.title)} />
        <div className='min-w-0 flex-1'>
          <div className='flex items-center gap-2'>
            <span className='truncate text-sm font-medium text-gray-900 dark:text-slate-100'>
              {t(current.title)}
            </span>
            {current.isNew && (
              <span className='rounded-full bg-primary-500/10 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-primary-600 dark:text-primary-300'>
                {t('New')}
              </span>
            )}
          </div>
          <p className='truncate text-xs text-gray-500 dark:text-slate-400'>
            {t(current.description)}
          </p>
        </div>
        <ChevronDownIcon
          className={clsxm(
            'h-4 w-4 flex-shrink-0 text-gray-400 transition-transform dark:text-slate-400',
            open && 'rotate-180',
          )}
        />
      </button>

      {open && (
        <div
          role='listbox'
          className='absolute left-0 right-0 z-20 mt-1 max-h-96 overflow-auto rounded-md border border-gray-200 bg-white py-1 shadow-lg dark:border-white/10 dark:bg-slate-800'
        >
          {actionsWithDescription.map((action) => {
            const isSelected = action.title === current.title;
            return (
              <button
                key={action.id}
                type='button'
                role='option'
                aria-selected={isSelected}
                onClick={() => {
                  onChange(action.title as ImageModelType);
                  setOpen(false);
                }}
                className={clsxm(
                  'flex w-full items-center gap-3 px-3 py-2 text-left transition-colors',
                  isSelected
                    ? 'bg-primary-50 dark:bg-primary-900/20'
                    : 'hover:bg-gray-50 dark:hover:bg-slate-700/60',
                )}
              >
                <Thumbnail src={action.image} alt={t(action.title)} />
                <div className='min-w-0 flex-1'>
                  <div className='flex items-center gap-2'>
                    <span
                      className={clsxm(
                        'truncate text-sm font-medium',
                        isSelected
                          ? 'text-primary-700 dark:text-primary-200'
                          : 'text-gray-900 dark:text-slate-100',
                      )}
                    >
                      {t(action.title)}
                    </span>
                    {action.isNew && (
                      <span className='rounded-full bg-primary-500/10 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-primary-600 dark:text-primary-300'>
                        {t('New')}
                      </span>
                    )}
                  </div>
                  <p className='line-clamp-1 text-xs text-gray-500 dark:text-slate-400'>
                    {t(action.description)}
                  </p>
                </div>
                {isSelected && (
                  <svg
                    className='h-4 w-4 flex-shrink-0 text-primary-600 dark:text-primary-300'
                    viewBox='0 0 20 20'
                    fill='currentColor'
                  >
                    <path
                      fillRule='evenodd'
                      d='M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z'
                      clipRule='evenodd'
                    />
                  </svg>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default memo(ActionSelection);
